// ** Ensure React is globally available FIRST
import './react-global.js'

// ** React Imports
import React, { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router } from 'react-router-dom'
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js'
// import { reactPlugin } from './services/appInsights'

// ** Redux Imports
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { store, persistor } from './_redux/store'

import { ThemeContext } from './utility/context/ThemeColors'
import RoutesManager from './router/RoutesManager'
import { ToastContainer } from 'react-toastify'
import { AuthenticatedTemplate } from '@azure/msal-react'

// ** PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx.min' //JSX
import 'prismjs/components/prism-javascript.min' // JavaScript
import 'prismjs/components/prism-python.min' // Python
import 'prismjs/components/prism-java.min' // Java
import 'prismjs/components/prism-css.min' // CSS
import 'prismjs/components/prism-bash.min' // Bash
import 'prismjs/components/prism-markup.min' // HTML/XML
import 'prismjs/components/prism-json.min' // JSON
import 'prismjs/components/prism-yaml.min' // YAML
import 'prismjs/components/prism-c.min' // C
import 'prismjs/components/prism-cpp.min' // C++
import 'prismjs/components/prism-go.min' // GO
import 'prismjs/components/prism-csharp.min' // C#
import 'prismjs/components/prism-ruby.min' // Ruby
import 'prismjs/components/prism-swift.min' // Swift
import 'prismjs/components/prism-kotlin.min' // Kotlin
import 'prismjs/components/prism-typescript.min' // TypeScript
import 'prismjs/components/prism-rust.min' // Rust
import 'prismjs/components/prism-sql.min' // SQL
import 'prismjs/components/prism-r.min' // R
import 'prismjs/components/prism-scala.min' // Scala
import 'prismjs/components/prism-perl.min' // Perl
import 'prismjs/components/prism-dart.min' // Dart
import 'prismjs/components/prism-lua.min' // Lua
import 'prismjs/components/prism-excel-formula.min' // excel
import 'prismjs/components/prism-graphql.min' // graphql
import 'prismjs/components/prism-regex.min' // regex
import 'prismjs/components/prism-tsx.min' // tsx
import 'prismjs/components/prism-sass.min' // sass
import 'prismjs/components/prism-scss.min' // scss

// ** Ripple Button
// import './@core/components/ripple-button'

// ** React Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css'

// ** Core styles
import './@core/assets/fonts/feather/iconfont.css'
import './@core/scss/core.scss'
import './assets/scss/style.scss'
import 'dsm-react-lib/dist/index.css'
import './global.css'
import 'react-loading-skeleton/dist/skeleton.css'
import 'react-tooltip/dist/react-tooltip.css'
import 'flatpickr/dist/flatpickr.css'

// ** Service Worker
import * as serviceWorker from './serviceWorker'
import { reactPlugin } from './_services/AppInsights'
import { appInsights } from './_services/AppInsights'
import { EventType } from './utility/types'
import { ThemeProvider } from 'styled-components'
import { mgInvestments } from './assets/styles/mgInvestments'
import { ErrorBoundary } from './router/ErrorBoundary'
import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig, loginRequest } from './authConfig'
import { MsalProvider } from '@azure/msal-react'

//**React Drag and drop
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { SplashScreen } from './SplashScreen'

// ** Initialize theme BEFORE React renders to prevent flash of wrong theme
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('centrali-theme') || 'system'
  const root = document.documentElement

  const shouldBeDark =
    savedTheme === 'dark' ||
    (savedTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  if (shouldBeDark) {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

initializeTheme()

const msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig)

// Reports React 19 root-level render errors (uncaught, boundary-caught, or recovered) to Application Insights.
const reportRootError = (source) => (error, errorInfo) => {
  appInsights.trackEvent({
    name: EventType.ErrorBoundary,
    properties: {
      source,
      error: error?.message ?? String(error),
      componentStack: errorInfo?.componentStack
    }
  })
}

const root = createRoot(document.getElementById('root'), {
  onCaughtError: reportRootError('onCaughtError'),
  onUncaughtError: reportRootError('onUncaughtError'),
  onRecoverableError: reportRootError('onRecoverableError')
})

const handleResponse = (response) => {
  let accountId = ''
  if (response !== null) {
    accountId = response.account.homeAccountId
  } else {
    const currentAccounts = msalInstance.getAllAccounts()
    if (currentAccounts.length === 0) {
      msalInstance.loginRedirect(loginRequest)
    } else if (currentAccounts.length === 1) {
      accountId = currentAccounts[0].homeAccountId
    }
  }
}

msalInstance.handleRedirectPromise().then(handleResponse)

root.render(
  <AppInsightsContext.Provider value={reactPlugin}>
    <Provider store={store}>
      <ErrorBoundary>
        <PersistGate loading={'Persist gate loading....'} persistor={persistor}>
          <Suspense fallback={<></>}>
            <ThemeContext>
              <ThemeProvider theme={mgInvestments}>
                <MsalProvider instance={msalInstance}>
                  <SplashScreen />
                  <Router>
                    <DndProvider backend={HTML5Backend}>
                      <AuthenticatedTemplate>
                        <RoutesManager />
                        <ToastContainer newestOnTop />
                      </AuthenticatedTemplate>
                    </DndProvider>
                  </Router>
                </MsalProvider>
              </ThemeProvider>
            </ThemeContext>
          </Suspense>
        </PersistGate>
      </ErrorBoundary>
    </Provider>
  </AppInsightsContext.Provider>
)

serviceWorker.register()
